import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Check, Heart, BookOpen, Users, Award, Clock, Target, Star } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const LandingPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hasTrackedViewContent, setHasTrackedViewContent] = useState(false);
  const contentSectionRef = useRef(null);

  // Función para disparar ViewContent (solo una vez por sesión)
  const trackViewContent = () => {
    if (!hasTrackedViewContent && typeof window.fbq === 'function') {
      window.fbq('track', 'ViewContent', {
        content_name: 'Querida mamá...',
        content_category: 'Curso Online',
        value: 44.99,
        currency: 'USD'
      });
      setHasTrackedViewContent(true);
    }
  };

  // IntersectionObserver para detectar scroll a la sección de contenido
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            trackViewContent();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (contentSectionRef.current) {
      observer.observe(contentSectionRef.current);
    }

    return () => {
      if (contentSectionRef.current) {
        observer.unobserve(contentSectionRef.current);
      }
    };
  }, [hasTrackedViewContent]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 90;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Función para manejar clic en "Ver Contenido"
  const handleViewContentClick = () => {
    trackViewContent();
    scrollToSection('contenido');
  };

  // Función para manejar navegación del carrusel (flechas)
  const handleCarouselNavigation = (direction) => {
    trackViewContent();
    if (direction === 'prev') {
      setCurrentSlide(prev => prev === 0 ? modules.length - 1 : prev - 1);
    } else {
      setCurrentSlide(prev => prev === modules.length - 1 ? 0 : prev + 1);
    }
  };

  // Función para manejar clic en dots del carrusel
  const handleDotClick = (index) => {
    trackViewContent();
    setCurrentSlide(index);
  };

  // Función para manejar el clic en botones de compra
  const handlePurchaseClick = () => {
    // Disparar evento InitiateCheckout de Meta Pixel
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Querida mamá...',
        content_category: 'Curso Online',
        value: 44.99,
        currency: 'USD'
      });
    }
    // Redirigir a Hotmart
    window.location.href = 'https://pay.hotmart.com/C103099414D?off=2589nq5j';
  };

  const modules = [
    {
      number: '01',
      title: 'Introducción a la Crianza Positiva aplicada a los Límites',
      description: 'Sentamos las bases del curso. Comprenderás qué es realmente la crianza positiva y por qué es el marco más efectivo para poner límites sanos.',
      objectives: [
        'Entender los pilares de la crianza positiva y cómo sostenerlos cuando dices "no"',
        'Diferenciar entre acompañar emociones y permitirlo todo',
        'Mantener el respeto y la conexión sin perder autoridad',
        'Aplicar la crianza positiva en situaciones reales del día a día'
      ],
      goal: 'Una base sólida y coherente desde la que aplicar todos los límites',
      color: 'accent-orange'
    },
    {
      number: '02',
      title: 'Autoridad Sana vs. Autoritarismo',
      description: 'Uno de los módulos más transformadores del curso. Descubrirás por qué muchos límites no funcionan y cómo cambiar el enfoque para que tu hijo coopere sin miedo ni sumisión.',
      objectives: [
        'Diferenciar claramente entre autoridad, autoritarismo y permisividad',
        'Entender cómo se siente un niño ante cada estilo educativo',
        'Detectar frases, actitudes y dinámicas que rompen la autoridad sana',
        'Posicionarte como figura de referencia segura, firme y calmada'
      ],
      goal: 'Recuperar tu seguridad como madre y ejercer una autoridad que educa, no que impone',
      color: 'accent-teal'
    },
    {
      number: '03',
      title: '¿Por qué mi hijo no me hace caso?',
      description: 'Abordamos una de las preguntas más repetidas por las familias. Errores comunes al poner límites.',
      objectives: [
        'Identificar los errores más frecuentes al establecer límites',
        'Entender el papel del desarrollo infantil en la obediencia',
        'Detectar incoherencias invisibles que debilitan los límites',
        'Ajustar expectativas realistas según la edad del niño'
      ],
      goal: 'Dejar de repetir sin resultados y empezar a ver cambios reales',
      color: 'accent-yellow'
    },
    {
      number: '04',
      title: 'Estrategias prácticas para establecer límites efectivos',
      description: 'El corazón práctico del curso. Estrategias claras, aplicables y respetuosas, pensadas para el día a día real de las familias.',
      objectives: [
        'Poner límites con claridad, calma y firmeza',
        'Actuar cuando hay rabietas, resistencia o desregulación',
        'Sostener el límite aunque tu hijo se enfade',
        'Usar el lenguaje adecuado para evitar luchas de poder',
        'Mantener el límite sin gritar, amenazar ni castigar'
      ],
      goal: 'Saber exactamente qué hacer y qué decir en los momentos difíciles',
      color: 'accent-pink'
    },
    {
      number: '05',
      title: 'Recursos Visuales para el Día a Día',
      description: 'Material descargable y visual para que no tengas que improvisar cada día.',
      objectives: [
        'Semáforo de los límites',
        'Tabla "Qué hacer cuando mi hijo se desregula"',
        'Mapa visual de los límites sanos',
        'Guía de consecuencias lógicas',
        'Plantilla de acuerdos de familia',
        'Recursos para rutinas, normas y convivencia'
      ],
      goal: 'Mantener los límites de forma clara, coherente y constante',
      color: 'accent-blue'
    }
  ];

  const empathyPoints = [
    'Quieres poner límites, pero dudas constantemente si lo estás haciendo bien',
    'Te prometiste no gritar ni castigar… y aun así a veces acabas haciéndolo',
    'Sientes que tu hijo/a no te escucha o te pone a prueba todo el tiempo',
    'Tu hijo no te hace caso hasta que no alzas la voz',
    'Quieres educar con respeto sin caer en la permisividad'
  ];

  const faqs = [
    {
      question: '¿Cuánto tiempo tengo acceso al curso?',
      answer: 'Tendrás acceso ilimitado de por vida al curso y a todas las actualizaciones futuras. Podrás revisarlo las veces que necesites.'
    },
    {
      question: '¿Es solo teoría o incluye casos prácticos?',
      answer: 'Durante los cuatro primeros módulos aprenderás cómo establecer límites mediante ejemplos reales y estrategias prácticas que podrás aplicar inmediatamente. Además, el quinto módulo incluye recursos descargables para ayudarte a poner en práctica todo lo aprendido en tu día a día.'
    },
    {
      question: '¿Qué edad deben tener mis hijos para aplicar este método?',
      answer: 'El curso está pensado principalmente para familias con niños de 1 a 6 años. Aunque el enfoque es la primera infancia, muchos de los aprendizajes pueden seguir siendo útiles más adelante.'
    },
    {
      question: '¿Y si no veo resultados inmediatos?',
      answer: 'Aprender nuevas herramientas lleva tiempo y práctica. Los límites necesitan repetirse y ajustarse para que funcionen. El curso te acompaña en ese proceso, ayudándote a aplicar los cambios de forma realista y respetuosa.'
    },
    {
      question: '¿Necesito conocimientos previos en crianza positiva?',
      answer: 'No, el curso está diseñado tanto para quienes se inician en crianza respetuosa como para quienes ya tienen conocimientos pero necesitan herramientas prácticas para los límites.'
    },
    {
      question: '¿Hay garantía de devolución?',
      answer: 'Sí, tienes 14 días de garantía. Si sientes que el curso no es para ti, te devolvemos el 100% de tu inversión sin preguntas.'
    }
  ];

  return (
    <div style={{ background: 'var(--bg-page)' }}>
      {/* Decorative SVG Lines */}
      <svg className="decorative-svg decorative-line-1" width="100" height="100" viewBox="0 0 100 100" fill="none">
        <path d="M10 50 Q 30 10, 50 50 T 90 50" stroke="#4A9B8E" strokeWidth="3" fill="none" opacity="0.3"/>
      </svg>
      <svg className="decorative-svg decorative-line-2" width="80" height="80" viewBox="0 0 80 80" fill="none">
        <circle cx="40" cy="40" r="30" stroke="#FF8C42" strokeWidth="3" fill="none" opacity="0.2"/>
        <path d="M40 10 L40 70 M10 40 L70 40" stroke="#FF8C42" strokeWidth="2" opacity="0.3"/>
      </svg>

      {/* Hero Section */}
      <section className="hero-section" style={{ paddingTop: '6rem' }}>
        <div className="hero-content" style={{ textAlign: 'center', width: '100%' }}>
          <div className="hero-badge">
            <Heart size={14} />
            <span>CURSO ONLINE · ACCESO INMEDIATO</span>
          </div>
          
          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>
            ¿ <span className="text-underline-orange">Cansada</span> de repetir las cosas mil veces y acabar gritando?
          </h1>
          
          <p className="body-large" style={{ 
            maxWidth: '750px', 
            margin: '0 auto 2rem',
            color: 'var(--text-secondary)'
          }}>
            Si estás cansada de repetir lo mismo mil veces y solo te hacen caso cuando levantas la voz… 
            
            No necesitas ser más dura. 
            Necesitas poner límites que tu hijo entienda. 
          </p>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem', 
            marginBottom: '2.5rem',
            width: '100%'
          }}>
            <button 
              onClick={() => scrollToSection('precio')} 
              className="btn-primary"
              style={{ fontSize: '1.0625rem', fontWeight: 700 }}
            >
              👇 Quiero que me escuchen a la primera
            </button>
            <button 
              onClick={handleViewContentClick} 
              className="btn-secondary"
              style={{ fontSize: '1rem' }}
            >
              Ver Contenido
            </button>
          </div>
          
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem',
            maxWidth: '100%',
            margin: '0 auto'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="stat-number">5</div>
              <div className="stat-label">MÓDULOS CLAVE</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="stat-number">∞</div>
              <div className="stat-label">ACCESO DE POR VIDA</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="stat-number">100%</div>
              <div className="stat-label">RECURSOS APLICABLES</div>
            </div>
          </div>
        </div>
      </section>

      {/* Empathy Section */}
      <section id="para-ti" style={{ padding: '6rem 0', background: 'var(--bg-card)' }}>
        <div className="container">
          <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Este curso es <span className="text-underline-orange">para ti</span> si...
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '4rem',
              color: 'var(--text-secondary)'
            }}>
              Te sientes identificada con alguna de estas situaciones
            </p>
            
            <div style={{ 
              display: 'grid', 
              gap: '2rem',
              textAlign: 'left'
            }}>
              {empathyPoints.map((point, index) => (
                <div 
                  key={index}
                  className="card hover-lift"
                  style={{
                    display: 'flex',
                    gap: '1.5rem',
                    alignItems: 'flex-start',
                    background: index % 2 === 0 ? 'var(--bg-card)' : 'var(--color-secondary-light)'
                  }}
                >
                  <div style={{
                    minWidth: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--color-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '0.25rem',
                    flexShrink: 0
                  }}>
                    <Check size={18} strokeWidth={3} color="white" />
                  </div>
                  <p className="body-large" style={{ fontWeight: 500 }}>{point}</p>
                </div>
              ))}
            </div>
            
            <div className="card" style={{
              marginTop: '4rem',
              padding: '3rem',
              background: 'linear-gradient(135deg, #FFF5EB 0%, #FFFFFF 100%)',
              border: '3px solid var(--color-primary)',
              textAlign: 'center'
            }}>
              <p className="heading-3" style={{ marginBottom: '1rem' }}>
                Si te has sentido identificada, escucha esto:
              </p>
              <p className="body-large">
                No necesitas ser más dura. Necesitas aprender a poner límites de forma que tu hijo quiera respetarlos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section Before Course Content */}
      <section style={{ 
        padding: '4rem 0', 
        background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(74, 155, 142, 0.1) 100%)',
        borderTop: '2px solid var(--color-secondary-light)',
        borderBottom: '2px solid var(--color-secondary-light)'
      }}>
        <div className="container">
          <div style={{ 
            maxWidth: '900px', 
            margin: '0 auto', 
            textAlign: 'center' 
          }}>
            <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
              Más de <span className="text-underline-orange">500</span> madres han dejado de gritar para que las escuchen
            </h2>
            
            <p className="body-large" style={{ 
              marginBottom: '2rem',
              color: 'var(--text-secondary)'
            }}>
              No porque ahora sean más estrictas. Sino porque aprendieron a ejercer una autoridad que da seguridad. ¡Ahora te toca a ti!
            </p>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <button 
                onClick={() => scrollToSection('precio')} 
                className="btn-primary"
                style={{ fontSize: '1.0625rem', fontWeight: 700 }}
              >
                Ver Oferta Exclusiva
              </button>
              
              <p className="body-small" style={{ 
                color: 'var(--text-muted)',
                marginTop: '0.5rem'
              }}>
                💫 Aprende a poner límites con seguridad
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content */}
      <section id="contenido" ref={contentSectionRef} style={{ padding: '6rem 0' }}>
        <div className="container">
          <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
              <span className="text-underline-orange">Contenido</span> del Curso
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Este curso está diseñado para acompañarte paso a paso en el proceso de poner límites claros, firmes y respetuosos, sin gritos, sin castigos y sin culpa.
            </p>
            <p className="heading-3" style={{ color: 'var(--color-primary)' }}>
              No es teoría suelta. Es una guía práctica para transformar el día a día en casa.
            </p>
          </div>
          
          {/* Carousel Container */}
          <div style={{ position: 'relative', maxWidth: '600px', margin: '0 auto' }}>
            {/* Carousel Track */}
            <div style={{ overflow: 'hidden', borderRadius: '1.5rem' }}>
              <div style={{
                display: 'flex',
                transition: 'transform 0.4s ease-in-out',
                transform: `translateX(-${currentSlide * 100}%)`
              }}>
                {modules.map((module, index) => (
                  <div 
                    key={index}
                    style={{ minWidth: '100%', padding: '0 0.5rem', boxSizing: 'border-box' }}
                  >
                    <div className={`module-card ${module.color}`}>
                      <div style={{ 
                        fontSize: '3rem', 
                        fontWeight: 900, 
                        opacity: 0.15,
                        marginBottom: '1rem',
                        lineHeight: 1
                      }}>
                        {module.number}
                      </div>
                      
                      <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                        {module.title}
                      </h3>
                      
                      <p className="body-medium" style={{ 
                        marginBottom: '2rem',
                        color: 'var(--text-secondary)'
                      }}>
                        {module.description}
                      </p>
                      
                      <div style={{ marginBottom: '2rem' }}>
                        <p className="body-small" style={{ 
                          fontWeight: 600, 
                          marginBottom: '1rem',
                          textTransform: 'uppercase',
                          fontSize: '0.75rem',
                          letterSpacing: '0.05em',
                          color: 'var(--color-primary)'
                        }}>
                          Aprenderás a:
                        </p>
                        <ul style={{ 
                          listStyle: 'none', 
                          padding: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.75rem'
                        }}>
                          {module.objectives.map((objective, idx) => (
                            <li key={idx} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                              <Check size={18} style={{ minWidth: '18px', marginTop: '0.25rem', color: 'var(--color-secondary)' }} />
                              <span className="body-small">{objective}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div style={{
                        padding: '1.5rem',
                        background: 'rgba(255, 255, 255, 0.7)',
                        borderRadius: '1rem',
                        marginTop: 'auto',
                        border: '2px solid var(--color-secondary-light)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                          <Target size={20} style={{ minWidth: '20px', marginTop: '0.25rem', color: 'var(--color-primary)' }} />
                          <div>
                            <p className="caption" style={{ 
                              fontWeight: 600, 
                              marginBottom: '0.5rem',
                              textTransform: 'uppercase',
                              color: 'var(--color-primary)'
                            }}>
                              Objetivo del módulo
                            </p>
                            <p className="body-small" style={{ fontWeight: 500 }}>{module.goal}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Controls - Below carousel */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '1.5rem',
              marginTop: '2rem'
            }}>
              {/* Left Arrow */}
              <button
                onClick={() => handleCarouselNavigation('prev')}
                style={{
                  background: 'white',
                  border: '2px solid var(--color-primary)',
                  borderRadius: '50%',
                  width: '44px',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  flexShrink: 0
                }}
              >
                <ChevronLeft size={22} style={{ color: 'var(--color-primary)' }} />
              </button>

              {/* Dot Indicators */}
              <div style={{
                display: 'flex',
                gap: '0.5rem'
              }}>
                {modules.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    style={{
                      width: currentSlide === index ? '2rem' : '0.625rem',
                      height: '0.625rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      background: currentSlide === index ? 'var(--color-primary)' : 'var(--border-primary)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      padding: 0
                    }}
                    aria-label={`Ir al módulo ${index + 1}`}
                  />
                ))}
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => handleCarouselNavigation('next')}
                style={{
                  background: 'white',
                  border: '2px solid var(--color-primary)',
                  borderRadius: '50%',
                  width: '44px',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  flexShrink: 0
                }}
              >
                <ChevronRight size={22} style={{ color: 'var(--color-primary)' }} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonios" style={{ padding: '6rem 0', background: 'var(--bg-card)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Lo que dicen las <span className="text-underline-orange">madres</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Madres que dejaron de gritar y empezaron a ser escuchadas.
            </p>
          </div>
          
          <div style={{ 
            maxWidth: '1000px', 
            margin: '0 auto',
            borderRadius: '1.5rem',
            overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)'
          }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_peaceful-limits/artifacts/82owsda4_testimonios.png"
              alt="Testimonios de madres"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="precio" style={{ padding: '4rem 0' }}>
        <div className="container">
          <div style={{ maxWidth: '850px', margin: '0 auto' }}>
            <div className="price-box" style={{ textAlign: 'center' }}>
              
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Deja de gritar para que te escuchen
              </h2>
              
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: '1rem',
                  marginBottom: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    fontSize: '3rem',
                    fontWeight: 900,
                    color: 'var(--color-primary)',
                    lineHeight: 1
                  }}>
                    $147
                  </span>
                </div>
                <p className="body-medium" style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                  O lo que vale una escapada de fin de semana. La diferencia es que la escapada dura un fin de semana, estas herramientas duran para siempre. 
                </p>
              </div>
              
              <div style={{ 
                marginBottom: '2rem',
                textAlign: 'left',
                padding: '1.75rem 1.5rem',
                background: 'white',
                borderRadius: '1.5rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
              }}>
                <p className="body-small" style={{ 
                  fontWeight: 700, 
                  marginBottom: '1.25rem',
                  textTransform: 'uppercase',
                  fontSize: '0.8125rem',
                  color: 'var(--color-primary)',
                  letterSpacing: '0.05em'
                }}>
                  ✓ Incluye:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {[
                    '5 Módulos completos con video-lecciones',
                    'Recursos descargables y plantillas prácticas',
                    'Acceso inmediato y de por vida',
                    'Actualizaciones futuras sin costo adicional',
                    'Garantía de devolución de 14 días',
                    '✨ Bonus Exclusivo: Guía para trabajar el autocontrol y así ahorrarte más de una rabieta.'
                  ].map((item, index) => (
                    <li key={index} style={{ display: 'flex', gap: '0.875rem', alignItems: 'flex-start' }}>
                      <Check size={18} style={{ minWidth: '18px', marginTop: '0.125rem', color: 'var(--color-secondary)', flexShrink: 0 }} strokeWidth={3} />
                      <span className="body-small" style={{ fontWeight: index === 5 ? 700 : 500, color: index === 5 ? '#DC3545' : 'inherit' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <button className="btn-primary" style={{ 
                width: '100%', 
                padding: '1.25rem',
                fontSize: '1.0625rem',
                fontWeight: 700
              }}
              onClick={handlePurchaseClick}
              >
                🎯 Inscribirme Ahora
              </button>
              
              <p className="body-small" style={{ marginTop: '1.25rem', color: 'var(--text-muted)' }}>
                🔒 Garantía de devolución de 14 días · Sin preguntas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bonus Section - Simplified */}
      <section style={{ 
        padding: '4rem 0', 
        background: 'var(--bg-card)'
      }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{
              background: 'linear-gradient(135deg, #FFF5EB 0%, #FFFFFF 100%)',
              border: '2px solid var(--color-primary)',
              borderRadius: '1.5rem',
              padding: '2.5rem 2rem',
              textAlign: 'center',
              boxShadow: '0 8px 30px rgba(255, 140, 66, 0.15)'
            }}>
              <div style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #DC3545 0%, #C82333 100%)',
                color: 'white',
                padding: '0.5rem 1.25rem',
                borderRadius: '2rem',
                fontWeight: 700,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '1.5rem'
              }}>
                ✨ Bonus Exclusivo
              </div>
              
              <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
                Guía para trabajar el autcontrol
              </h2>
              
              <p className="body-large" style={{ 
                marginBottom: '2rem',
                color: 'var(--text-secondary)',
                maxWidth: '550px',
                margin: '0 auto 2rem'
              }}>
                Sin autocontrol, no hay gestión de emociones. El autocontrol te permite: <strong>Pensar antes de actuar</strong>, <strong>reconocer la emoción</strong> y <strong>pensar una solución para gestionar la emoción correctamente</strong>. Es el primer paso para gestionar las emociones.
              </p>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                marginBottom: '1rem',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  textDecoration: 'line-through',
                  color: 'var(--text-muted)'
                }}>
                  $20
                </div>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 900,
                  color: '#DC3545'
                }}>
                  GRATIS
                </div>
              </div>
              
              <p className="body-small" style={{ 
                color: 'var(--text-muted)'
              }}>
                Estás a un paso más cerca de reducir las rabietas. 
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ padding: '6rem 0', background: 'var(--bg-card)' }}>
        <div className="container">
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
                <span className="text-underline-orange">Preguntas</span> Frecuentes
              </h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Resolvemos tus dudas más comunes
              </p>
            </div>
            
            <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)' }}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="body-medium" style={{ 
                      fontWeight: 600,
                      textAlign: 'left',
                      color: 'var(--text-primary)'
                    }}>
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="body-medium" style={{ 
                      color: 'var(--text-secondary)',
                      paddingTop: '0.5rem',
                      lineHeight: 1.6
                    }}>
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: '6rem 0', background: 'linear-gradient(135deg, #FFF5EB 0%, #F0F9F7 100%)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '600px',
          background: 'var(--color-secondary-light)',
          borderRadius: '50%',
          opacity: 0.2,
          zIndex: 0
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
              No necesitas gritar más fuerte. <span className="text-underline-orange">Necesitas</span> límites más claros
            </h2>
            
            <p className="body-large" style={{ 
              marginBottom: '3rem',
              color: 'var(--text-secondary)'
            }}>
              Deja atrás los gritos, la culpa y la incertidumbre. Aprende a educar con respeto y firmeza desde hoy mismo.
            </p>
            
            <button 
              onClick={handlePurchaseClick} 
              className="btn-primary"
              style={{ padding: '1.5rem 3.5rem', fontSize: '1.125rem', fontWeight: 700 }}
            >
              LO QUIERO TODO Y LO QUIERO YA
            </button>
            
            <p className="body-small" style={{ marginTop: '2rem', color: 'var(--text-muted)' }}>
              ⚡ Acceso inmediato · 🔒 Garantía de 14 días
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
